import { State } from '../state';
import { someMatchingEmailDomain } from '../../utils/userApiHelper';
import { Entity, MembershipLevel } from '../../userApi';
import { hasCompetitionStarted } from './competition';
import logger from '../../utils/logger';

export const userEmailPermittedByCompetition = (state: State) => {
  const { competition, userEmails, user } = state;
  if (!user || !user.email || !userEmails || !competition) {
    return;
  }

  const primaryUserEmail = userEmails.find((userEmail) => userEmail.email === user.email);
  if (primaryUserEmail) {
    const primaryPermittedCompDomain = someMatchingEmailDomain(primaryUserEmail.email, competition.permittedDomains);
    if (primaryPermittedCompDomain) {
      return primaryUserEmail;
    }
  }

  return userEmails.find((userEmail) => someMatchingEmailDomain(userEmail.email, competition.permittedDomains));
};

export const onlyVerifiedCompetitionUserEmail = (state: State) => {
  const { competition, competitionUser, userEmails } = state;
  if (!competitionUser || !competitionUser.verified || !userEmails || !competition) return;

  const verifiedCompetitionUserEmails = userEmails.filter((userEmail) =>
    userEmail.verified &&
    someMatchingEmailDomain(userEmail.email, competition.permittedDomains)
  );
  if (verifiedCompetitionUserEmails.length > 1) return;
  return verifiedCompetitionUserEmails[0];
};

export const hasEmailPermittedByCompetition = (state: State) =>
  Boolean(userEmailPermittedByCompetition(state));

export const isVerifiedCompetitionUser = (state: State) =>
  state.competitionUser && state.competitionUser.verified;

export const isCompetitionUser = (state: State) =>
  Boolean(state.competitionUser && state.competition &&
    state.competitionUser.competitionID == state.competition.id);

export const isCompetitionAdmin = (state: State) => {
  const level = state.competitionUser && state.competitionUser.level;
  return level === MembershipLevel.Admin || level === MembershipLevel.Superadmin;
};

export const isCompetitionSuperadmin = (state: State) =>
  Boolean(state.competitionUser && state.competitionUser.level === MembershipLevel.Superadmin);

/** Bypass score-level and onboarding locks (QA catalog / close-out survey). */
export const canBypassPledgeParticipantRestrictions = (state: State) =>
  isCompetitionSuperadmin(state) ||
  userIsSomeUniversityAdminFromEntities(state);

const userIsSomeUniversityAdminFromEntities = (state: State) => {
  const { userMemberships, entities } = state;
  if (!entities) {
    return false;
  }
  return Object.keys(userMemberships || {}).some((entityID) => {
    const entity = entities[entityID];
    const membership = userMemberships[entityID];
    return entity &&
      entity.type === Entity.TypeEnum.University &&
      membership &&
      membership.status === 'accepted' &&
      (membership.level === MembershipLevel.Admin ||
        membership.level === MembershipLevel.Superadmin);
  });
};

// Division Captain = accepted admin/superadmin on a team entity (CCSF Division).
export const isDivisionCaptain = (state: State) => {
  const { userMemberships, entities } = state;
  if (!entities || !userMemberships) {
    return false;
  }
  return Object.keys(userMemberships).some((entityID) => {
    const entity = entities[entityID];
    const membership = userMemberships[entityID];
    return entity &&
      entity.type === Entity.TypeEnum.Team &&
      membership &&
      membership.status === 'accepted' &&
      (membership.level === MembershipLevel.Admin ||
        membership.level === MembershipLevel.Superadmin);
  });
};

// Pre-launch access: same roles as comms/admin tooling (matches API userIsCompetitionAdmin).
export const canBypassCompetitionStartRequirement = (state: State) =>
  isCompetitionAdmin(state) ||
  userIsSomeUniversityAdminFromEntities(state) ||
  isDivisionCaptain(state);

export const canViewAdminTools = (state: State) =>
  isCompetitionAdmin(state) || userIsSomeUniversityAdminFromEntities(state);

// Send Message + Moderation: competition admins and Division Captains.
export const canViewCommsTools = (state: State) =>
  canViewAdminTools(state) || isDivisionCaptain(state);

export const isUniversitySuperadmin = (state: State) => {
  const { userMemberships, entities } = state;
  if (!entities) {
    return false;
  }
  return Object.keys(userMemberships || {}).some((entityID) => {
    const entity = entities[entityID];
    return entity &&
      entity.type === Entity.TypeEnum.University &&
      userMemberships[entityID].level === MembershipLevel.Superadmin;
  });
};

export const isHealthSystemsEmployee = (state: State) => {
  const { user } = state;
  return Boolean(user) && Boolean(user.options) && user.options.isHealthFacilityEmployee;
};

export const canViewCompetitionContent = (state: State) => {
  if (canBypassCompetitionStartRequirement(state)) {
    return true;
  }
  if (!isVerifiedCompetitionUser(state)) {
    return false;
  }
  return hasCompetitionStarted(state);
};

export const profileUser = (state: State, required: boolean = false) => {
  const profileID = state.storyQuery.userID;
  if (!profileID) return;
  return someUser(state, profileID, required);
};

export const someUser = (state: State, userID: number, required: boolean = false) => {
  if (!userID && userID !== 0) {
    return;
  }
  if (state.user && Number(userID) === Number(state.user.id)) {
    return state.user;
  }
  const users = state.users || {};
  const user = users[userID as any] || users[String(userID)];
  if (!user && required) {
    logger.error(new Error(`Could not find user ${userID}.`));
  }
  return user;
};
